import { AgenturModel } from 'app/models/agentur.model';
import { ISelectOpt } from 'app/components/common/Select';
import { formatBMToMonthName, formatKw } from 'app/utils';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment';
import { AgenturStore } from '../agentur.store';
import { AgtLeadNowConvModel, AgtLeadNowConvStore, ILeadNowConvTotals } from '../agt.leadnow.conv.store';
import { BaseUiStore } from './base.ui.store';

export class LeadNowConvVM {
	agt: AgenturModel;

	@observable
	item: AgtLeadNowConvModel;

	constructor(agt: AgenturModel, data: AgtLeadNowConvModel) {
		makeObservable(this);
		this.agt = agt;
		this.item = data;
	}

	@computed
	get agtId() {
		return this.agt.agtId;
	}

	@computed
	get kw() {
		return this.item ? this.item.kw : 0;
	}

	@computed
	get brutto() {
		return this.item ? this.item.brutto : 0;
	}

	@computed
	get netto() {
		return this.item ? this.item.netto : 0;
	}

	@computed
	get converted() {
		return this.item ? this.item.converted : 0;
	}

	@computed
	get neuStck() {
		return this.item ? this.item.neuStck : 0;
	}

	@computed
	get neuBew() {
		return this.item ? this.item.neuBew : 0;
	}

	@computed
	get conversionRate() {
		if (!this.netto) return 0;
		return (this.converted / this.netto) * 100;
	}
}

export interface ILeadNowConvMonthlyHistory {
	bm: string;
	bmRaw: number;
	brutto: number;
	netto: number;
	converted: number;
	neuStck: number;
	neuBew: number;
}

// A kw belongs to the month containing its Thursday (ISO 8601 week-to-month rule).
const kwToBm = (kw: number): number => {
	const year = Math.floor(kw / 100);
	const week = kw % 100;
	const thursday = moment().year(year).isoWeek(week).isoWeekday(4);
	return thursday.year() * 100 + (thursday.month() + 1);
};

export class AgtLeadNowConvUiStore extends BaseUiStore<LeadNowConvVM> {
	name = 'AgtLeadNowConvUiStore';
	agtLeadNowConvStore: AgtLeadNowConvStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, agtLeadNowConvStore: AgtLeadNowConvStore) {
		super();
		makeObservable(this);
		this.agtLeadNowConvStore = agtLeadNowConvStore;
		this.agenturStore = agenturStore;
	}

	@observable
	kws: number[] = [];

	@computed
	get kwsOpts(): ISelectOpt[] {
		return this.kws.map((kw) => ({
			value: kw,
			label: formatKw(kw),
		}));
	}

	@computed
	get minKw(): number {
		return this.minKwCurrent ?? 0;
	}

	@computed
	get maxKw(): number {
		return this.maxKwCurrent ?? 0;
	}

	@observable
	minKwCurrent?: number;

	@observable
	maxKwCurrent?: number;

	// Anonymous benchmark totals for the funnel reference lines. vdBenchmark is the
	// average of the relevant VD (undefined when no single VD anchor applies); abv
	// is the company-wide average. Both come from the unscoped benchmark endpoint.
	@observable
	vdBenchmark?: ILeadNowConvTotals;

	@observable
	abvBenchmark?: ILeadNowConvTotals;

	@action
	async loadBenchmark(minKw: number, maxKw: number, vdId?: string): Promise<void> {
		const cacheKey = `AgtLeadNowConvUiStore.benchmark${minKw}-${maxKw}-${vdId ?? 'all'}`;
		const res = await this.withCache(cacheKey, () => this.agtLeadNowConvStore.getBenchmark(minKw, maxKw, vdId), {
			cacheResult: true,
		});

		runInAction(() => {
			this.abvBenchmark = res.abv;
			this.vdBenchmark = res.vd;
		});
	}

	async _baseLoad() {
		await this.agenturStore.findAll();
		const distinctKws = await this.agtLeadNowConvStore.getDistinctKWs();

		runInAction(() => {
			this.kws = distinctKws;

			// Default range: first kw of current year (or earliest available) → max kw
			if (this.kws.length > 0 && !this.maxKwCurrent && !this.minKwCurrent) {
				const maxKw = this.kws[this.kws.length - 1];
				const currentYear = new Date().getFullYear();
				const yearStart = currentYear * 100 + 1;
				const firstKwOfYear = this.kws.find((kw) => kw >= yearStart) ?? this.kws[0];

				this.maxKwCurrent = maxKw;
				this.minKwCurrent = firstKwOfYear;
			}
		});
	}

	// Monthly funnel history for one agt or an aggregated set of agts, across all
	// available kws. Weekly rows are fetched per kw (scoped by the analytics
	// endpoint) and summed client-side into months.
	async loadConvHistoryByMonth(ids: number | number[]): Promise<ILeadNowConvMonthlyHistory[]> {
		const idList = Array.isArray(ids) ? [...ids].sort() : [ids];
		const cacheKey = `AgtLeadNowConvUiStore.loadConvHistoryByMonth_${idList.join('_')}`;

		return this.withCache(
			cacheKey,
			async () => {
				await this.load();
				const idSet = new Set(idList);
				const byBm = new Map<number, ILeadNowConvMonthlyHistory>();

				await Promise.all(
					this.kws.map(async (kw) => {
						const rows = await this.agtLeadNowConvStore.findByKw(kw);
						const bmRaw = kwToBm(kw);

						let entry = byBm.get(bmRaw);
						if (!entry) {
							entry = { bm: formatBMToMonthName(bmRaw), bmRaw, brutto: 0, netto: 0, converted: 0, neuStck: 0, neuBew: 0 };
							byBm.set(bmRaw, entry);
						}

						for (const row of rows) {
							if (!idSet.has(row.agtId)) {
								continue;
							}
							entry.brutto += row.brutto;
							entry.netto += row.netto;
							entry.converted += row.converted;
							entry.neuStck += row.neuStck;
							entry.neuBew += row.neuBew;
						}
					}),
				);

				return Array.from(byBm.values()).sort((a, b) => a.bmRaw - b.bmRaw);
			},
			{ cacheResult: true },
		);
	}

	@action
	async aggregateAgtsByKw(minKw: number, maxKw: number): Promise<LeadNowConvVM[]> {
		const cacheKey = `AgtLeadNowConvUiStore.aggregateAgtsByKw${minKw}-${maxKw}`;

		const res = await this.withCache(
			cacheKey,
			async () => {
				await this.load();

				const agenturen = this.agenturStore.agenturenWithoutAda;
				const data = await this.agtLeadNowConvStore.aggregateByKwRange(minKw, maxKw);

				const vms: LeadNowConvVM[] = [];
				agenturen.forEach((agt) => {
					const agtData = data.find((d) => d.agtId === agt.agtId);
					if (agtData) {
						vms.push(new LeadNowConvVM(agt, agtData));
					}
				});
				return vms;
			},
			{ cacheResult: true },
		);

		runInAction(() => {
			this.items = res;
		});

		return res;
	}
}
